// src/utils/apiUtils.js (or similar)

import { API } from "@aws-amplify/api"; // Need Amplify to get config
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloClient } from "apollo-client";
import { ApolloLink, split } from "apollo-link";
import { createHttpLink } from "apollo-link-http";
import { WebSocketLink } from "apollo-link-ws"; // v2 import path
import { getMainDefinition } from "apollo-utilities";
import envConfig from "../../envConfig";
import { generateRowId } from "../Utility";
const isWebSocketSubs = true;
const isElectron = !!window?.electronAPI;
let appSyncHost;
const urlObject = new URL(envConfig?.REACT_APP_GRAPHQL_ENDPOINT); // Parse the HTTP URL
if (urlObject.protocol !== "https:") {
  throw new Error("Expected HTTPS URL");
}
appSyncHost = urlObject.host;
let appsyncUrlRealTime;
let websocketObj;

export const callAsyncFunction = async (asyncFn, ...args) => {
  try {
    if (typeof asyncFn !== "function") {
      console.log("The first argument must be a function");
      return null;
    }
    let result;
    if (asyncFn) result = await asyncFn(...args);
    return result;
  } catch (error) {
    console.error("Error calling async function:", error);
    throw error;
  }
};

const openWebsocket = () => {
  const payload = {};

  return new Promise((resolve, reject) => {
    AppAuth.getCurrentIdToken()
      .then((token) => {
        const adapt_token = createBase64Header(token, appSyncHost);
        const base64Payload = btoa(JSON.stringify(payload));
        appsyncUrlRealTime = `${envConfig.REACT_APP_REALTIME_ENDPOINT}?header=${adapt_token}&payload=${base64Payload}`;
        const websocketObj = new WebSocket(appsyncUrlRealTime, ["graphql-ws"]);
        resolve(websocketObj);
      })
      .catch((error) => {
        console.error("Error opening WebSocket:", error);
        reject(error);
      });
  });
};

const runWebsocket = async (query, variables, observer) => {
  const ws = await openWebsocket();
  const sendMessage = (obj) => {
    ws.send(JSON.stringify(obj));
  };

  let initializingMode = true;

  ws.onopen = () => {
    sendMessage({ type: "connection_init" });
  };

  ws.onmessage = async (event) => {
    const data = JSON.parse(event.data);
    if (initializingMode) {
      if (data.type === "connection_ack") {
        // Acknowledge received, start subscribing

        // Get the Cognito User Pool ID token
        const idToken = await AppAuth.getCurrentIdToken(); // Implement this function to retrieve the ID token
        const jsonString = JSON.stringify({
          query: query,
          variables: variables,
        });
        // const wrappedSubscription =
        //   '{"query":"subscription onUpdateWorkspace {\\n onUpdateWorkspace {\\n __typename\\n id\\n }\\n }","variables":{}}';
        sendMessage({
          id: `${generateRowId(4)}`, // Use the ID token from Cognito User Pools
          type: "start",
          payload: {
            data: jsonString,
            extensions: {
              authorization: {
                Authorization: idToken,
                host: appSyncHost,
                "x-amz-user-agent": "aws-amplify/4.7.14 js",
              },
            },
          },
        });

        initializingMode = false;
      }
    } else {
      // Handle subscription data
      if (data.type === "data") {
        const subscriptionData = data;
        observer.next({ value: subscriptionData?.payload });
      } else if (data.type == "ka") {
        if (observer?.connect) observer?.connect();
      }
    }
  };

  ws.onerror = (error) => {
    observer.error(error);
  };

  ws.onclose = () => {
    if (observer?.close) observer?.close();
  };

  return {
    ...ws,
    unsubscribe: () => {
      ws.onopen = () => {};
      ws.onmessage = () => {};
      ws.onerror = () => {};
      ws.onclose = () => {};
      ws.close();
    },
  };
};

const createBase64Header = (authToken, host) => {
  const headerObj = {
    Authorization: authToken,
    host,
  };

  const base64Header = btoa(JSON.stringify(headerObj));

  return base64Header;
};

/**
 * Makes a GraphQL request, handling authentication for both Electron and Web.
 * @param {object} operation The result of graphqlOperation(query, variables)
 * @returns {Promise<object>} The GraphQL response data
 * @throws If the request fails or the user is not authenticated
 */
export const makeGraphQLRequest = async (operation) => {
  if (!operation?.query) {
    throw new Error("Invalid GraphQL operation provided.");
  }

  if (isElectron) {
    // --- Electron Flow: Manual Fetch ---
    console.debug("GraphQL: Making request via manual fetch (Electron)...");
    const idToken = await AppAuth.getCurrentIdToken();

    if (!idToken) {
      console.error(
        "GraphQL: Cannot make request in Electron, no valid ID token."
      );
      throw new Error("User not authenticated or session expired."); // Or handle as needed
    }
    // Adjust path based on your actual Amplify config structure
    const endpoint = envConfig.REACT_APP_GRAPHQL_ENDPOINT;

    if (!endpoint) {
      console.error("GraphQL: AppSync endpoint not found in Amplify config.");
      throw new Error("API endpoint configuration error.");
    }

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken, // Use the ID Token here
        },
        body: JSON.stringify(operation),
      });

      const responseBody = await response.json();

      if (!response.ok || responseBody.errors) {
        console.error(
          "GraphQL Error Response (Electron):",
          responseBody.errors || `HTTP Status ${response.status}`
        );
        // Extract specific error message if available
        const errorMessage =
          responseBody?.errors?.[0]?.message ||
          `Request failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      console.debug("GraphQL Response (Electron):", responseBody);
      return responseBody; // Return just the data part
    } catch (error) {
      console.error("GraphQL Fetch Error (Electron):", error);
      // Re-throw network errors or formatted GraphQL errors
      throw error;
    }
  } else {
    // --- Web Flow: Use Amplify API ---
    try {
      // Let Amplify handle auth automatically
      const response = await API.graphql(operation);
      // Amplify's API.graphql might throw on errors or return them in the response
      // Check the structure if needed, but often it throws directly for bad requests
      if (response.errors) {
        console.error("GraphQL Error Response (Web):", response.errors);
        throw new Error(
          response.errors[0]?.message || "GraphQL request failed"
        );
      }
      return response;
    } catch (error) {
      console.error("GraphQL Amplify API Error (Web):", error);
      // Handle Amplify specific errors, e.g., Auth errors if session becomes invalid
      if (error.message?.includes("No current user")) {
        throw new Error("User not authenticated.");
      }
      throw error; // Re-throw other errors
    }
  }
};

let electronAppSyncClient = null;

export const initializeElectronAppSyncClient = () => {
  // Return existing instance if already created
  if (electronAppSyncClient) {
    return electronAppSyncClient;
  }

  console.log(
    "Initializing ApolloClient v2 for Electron (HTTP + WebSocket)..."
  );

  // --- Configuration ---
  const httpUrl = envConfig?.REACT_APP_GRAPHQL_ENDPOINT;
  const region = envConfig?.REACT_APP_aws_appsync_region; // Keep region info if needed elsewhere // Explicitly using Cognito

  if (!httpUrl) {
    // httpUrl is essential for deriving host and setting up HTTP link
    console.error(
      "Cannot initialize ApolloClient: Missing AppSync HTTP URL (REACT_APP_GRAPHQL_ENDPOINT) in config."
    );
    throw new Error(
      "Missing AppSync HTTP URL configuration for Electron client."
    );
  }

  // --- Derive WebSocket URL and Extract Host ---
  let wsUrl;
  let appSyncHost;
  let _token_; // Variable to store the host name

  AppAuth.getCurrentIdToken()
    .then((token) => {
      const urlObject = new URL(httpUrl); // Parse the HTTP URL
      if (urlObject.protocol !== "https:") {
        throw new Error("Expected HTTPS URL");
      }
      appSyncHost = urlObject.host; // Extract the host (e.g., 'xxxx.appsync-api.region.amazonaws.com')
      // Construct the standard AppSync WebSocket URL
      // Note: The path is typically '/graphql' for the realtime endpoint as well,
      // unless specifically configured otherwise in AppSync. '/realtime' was an older pattern.
      // Let's stick to '/graphql' based on your hardcoded example, but verify if needed.
      wsUrl = `wss://${appSyncHost}/graphql&${token}`; // Use the extracted host
      console.log(`AppSync Host: ${appSyncHost}`);
      console.log(`Derived WebSocket URL: ${wsUrl}`);
    })
    .catch((error) => {
      console.error(
        `Failed to derive WebSocket URL/Host from ${httpUrl}:`,
        error
      );
      throw new Error(`Invalid AppSync HTTP URL format: ${httpUrl}`);
    });

  // --- Authentication Token Function ---
  // Fetches the Cognito ID token dynamically
  const getToken = async () => {
    console.log("getToken: Requesting ID token...");
    try {
      const token = await AppAuth.getCurrentIdToken();
      _token_ = token; // Use your imported module
      if (!token) {
        console.warn("getToken: No ID token available.");
        return null;
      }
      console.log("getToken: ID token obtained.");
      return token; // Return raw ID token string
    } catch (error) {
      console.error("getToken: Error fetching ID token:", error);
      return null; // Return null on error
    }
  };

  // --- Create HTTP Link ---
  // Handles Queries and Mutations
  const httpLink = createHttpLink({
    uri: httpUrl, // Use the main GraphQL endpoint URL
    // Headers will be set dynamically via authMiddleware
  });
  let wsLink;
  const extensions = {
    my_ets: "test",
  };
  AppAuth.getCurrentIdToken()
    .then((token) => {
      _____token = token;
      const adapt_token = createBase64Header(_____token, appSyncHost);
      wsLink = new WebSocketLink({
        uri: `${envConfig.REACT_APP_REALTIME_ENDPOINT}/graphql?header=${adapt_token}&payload=e30=`, // Use the derived WebSocket URL
        options: {
          reconnect: true, // Automatically attempt to reconnect on disconnect
          lazy: true, // Establish connection only when first subscription starts
        },
        connectionParams: {
          // Include the custom extensions object in the connectionParams
          extensions: extensions,
        },
      });
      // --- Create WebSocket Link ---
      // Handles Subscriptions

      // --- Create Authentication Link (Middleware) ---
      // Injects the Authorization header for HTTP requests (Queries/Mutations)
      const removeOperationNameLink = new ApolloLink((operation, forward) => {
        // Remove the operationName from the context
        const context = { ...operation.getContext() };

        // Create a new operation with the modified context
        const modifiedOperation = operation.setContext(context);

        // Forward the modified operation
        return forward(modifiedOperation);
      });

      const authMiddleware = new ApolloLink(async (operation, forward) => {
        // Don't fetch token unnecessarily if it's a subscription (handled by wsLink connectionParams)
        // Note: This optimization might be premature if you have unauth'd queries/mutations too.
        // Keep simple for now: always fetch and set header if available.
        const token = await getToken();

        // Dynamically set the context with authorization header
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers, // Preserve existing headers
            // Add Authorization header only if token exists
            ...(token ? { Authorization: token } : {}),
          },
        }));

        // Proceed to the next link in the chain (httpLink)
        return forward(operation);
      });

      // Combine the links
      const combinedLink = removeOperationNameLink.concat(authMiddleware);
      // --- Split Link ---
      // Directs traffic: subscriptions to wsLink, others to authMiddleware -> httpLink
      const splitLink = split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          const isSubscription =
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription";
          // console.log(`splitLink: Routing operation. Is Subscription? ${isSubscription}`);
          return isSubscription;
        },
        wsLink, // Target for subscriptions
        combinedLink.concat(httpLink) // Target for queries and mutations
      );

      // --- Create Apollo Cache ---
      const cache = new InMemoryCache();
      // --- Initialize Apollo Client (v2) ---
      try {
        electronAppSyncClient = new ApolloClient({
          link: splitLink, // Use the combined link logic
          cache: cache, // Use the configured cache
        });

        console.log(
          "ApolloClient v2 for Electron initialized successfully (HTTP + WebSocket with correct WS Auth)."
        );
        return electronAppSyncClient;
      } catch (error) {
        console.error(
          "Error initializing ApolloClient v2 for Electron:",
          error
        );
        electronAppSyncClient = null; // Reset singleton on error
        throw error; // Re-throw error for upstream handling
      }
    })
    .catch((error) => {
      throw new Error(`Invalid token`);
    });
  return electronAppSyncClient;
};

/**
 * Creates a GraphQL subscription, handling authentication for both Electron and Web.
 * @param {object} operation The result of graphqlOperation(query, variables)
 * @param {object} observer An object with next() and error() callbacks.
 * @returns {object} A subscription object with an unsubscribe() method.
 */
export const subscribeGraphQL = (operation, observer) => {
  if (
    !operation?.query ||
    !observer ||
    typeof observer.next !== "function" ||
    typeof observer.error !== "function"
  ) {
    process.env.REACT_APP_ENV === "Dev" &&
      console.error(
        "Invalid arguments provided to subscribeGraphQL.",
        operation,
        observer
      );
    // Return a dummy subscription object to prevent crashes downstream
    return {
      unsubscribe: () => {
        console.warn("Dummy unsubscribe called.");
      },
    };
  }

  if (isWebSocketSubs) {
    return runWebsocket(operation.query, operation.variables, observer);
  } else {
    // --- Web Flow: Use Amplify API ---

    try {
      return new Promise((resolve, reject) => {
        const subscription = API.graphql(operation).subscribe({
          next: (data) => {
            // Amplify wraps data in { value: { data: { ... } } }
            observer.next(data); // Forward directly
          },
          error: (error) => {
            console.error("GraphQL Subscription Error (Web):", error);
            reject(error); // Reject the Promise with the error
          },
          complete: () => {
            console.debug("GraphQL Subscription Complete (Web)");
            if (observer.complete) observer.complete(); // Forward if provided
          },
          close: () => {
            console.log("GraphQL Subscription Closed (Web)");
            if (observer.close) observer.close(); // Forward if provided
          },
          connect: () => {
            console.log("GraphQL Subscription Connected (Web)");
            if (observer.connect) observer.connect(); // Forward if provided
          },
        });

        resolve(subscription); // Resolve the Promise with the subscription object
      });
    } catch (error) {
      console.error("Error setting up Web GraphQL subscription:", error);
      // Invoke the error handler immediately if setup fails
      observer.error(error);
      return { unsubscribe: () => {} }; // Return dummy object
    }
  }
};
